// HandlePicker — one-time URL owner-handle capture.
//
// WS-B Chunk 6 UI. Handles are chosen once, at first event creation
// (like a GitHub username). No mid-account changes at launch. The URL
// contract `tournyflex.com/e/<handle>/<year>/<slug>` breaks the moment
// a handle moves, so this component only appears when the caller has
// no handle set (players.handle IS NULL). Once submitted successfully,
// the parent should re-fetch the player row so the handle picker
// disappears on the next mount.
//
// Real-time availability check as the user types (debounced 350ms).
// The server's write-time check is still authoritative — this is
// just fast feedback.
//
// Props:
//   suggestedFrom  — optional string to seed the input from (usually the
//                    player's name). We slugify it to a hint, not a
//                    default value — users often want to type their own.
//   onDone         — called with { handle } after a successful set.
//                    Parent typically refetches the player row and moves
//                    to the next step of the flow.

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'

const CHECK_DEBOUNCE_MS = 350

function slugifyHint(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/, '')
}

export default function HandlePicker({ suggestedFrom, onDone }) {
  const [handle, setHandle] = useState('')
  const [check, setCheck] = useState({ state: 'idle', reason: null })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const suggestion = suggestedFrom ? slugifyHint(suggestedFrom) : null

  useEffect(() => {
    const trimmed = handle.trim().toLowerCase()
    if (!trimmed) { setCheck({ state: 'idle', reason: null }); return }
    setCheck({ state: 'checking', reason: null })
    const t = setTimeout(async () => {
      try {
        const { data } = await db('check_handle_available', { handle: trimmed })
        if (data?.available) {
          setCheck({ state: 'available', reason: null })
        } else {
          setCheck({ state: 'unavailable', reason: data?.reserved ? 'reserved' : data?.reason || 'taken' })
        }
      } catch {
        setCheck({ state: 'idle', reason: null })
      }
    }, CHECK_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [handle])

  const submit = async () => {
    const value = handle.trim().toLowerCase()
    if (!value || busy || check.state !== 'available') return
    setBusy(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setError('Please sign in again to continue.')
        setBusy(false)
        return
      }
      const res = await fetch('/.netlify/functions/set-handle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ handle: value }),
      })
      const json = await res.json()
      if (!res.ok) {
        // If someone claimed it in the race window, re-run the availability
        // check so the user sees the unavailable state immediately.
        if (json.code === 'taken') {
          setCheck({ state: 'unavailable', reason: 'taken' })
          setError('Someone just took that handle — please try another.')
        } else {
          setError(json.error || 'Could not save handle.')
        }
        setBusy(false)
        return
      }
      onDone?.({ handle: json.handle })
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setBusy(false)
    }
  }

  const feedback = (() => {
    switch (check.state) {
      case 'idle':        return null
      case 'checking':    return <span className="text-muted text-xs">Checking…</span>
      case 'available':   return <span className="text-xs" style={{ color: 'var(--electric-green, #39FF14)' }}>Available</span>
      case 'unavailable':
        if (check.reason === 'reserved') return <span className="text-xs" style={{ color: 'var(--danger, #b23)' }}>Reserved word — try something else.</span>
        if (check.reason === 'bad_format') return <span className="text-xs" style={{ color: 'var(--danger, #b23)' }}>3–32 chars; a–z, 0–9, hyphens; can't start or end with a hyphen.</span>
        return <span className="text-xs" style={{ color: 'var(--danger, #b23)' }}>Taken.</span>
      default: return null
    }
  })()

  return (
    <div className="card">
      <h3 style={{ margin: 0, marginBottom: 6 }}>Pick your handle</h3>
      <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
        Your handle is the short name that appears in your event URLs
        (<code>tournyflex.com/e/<strong>you</strong>/2026/…</code>).
        You can only set it once — pick carefully.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span className="text-xs text-muted" style={{ fontFamily: 'var(--font-mono)' }}>tournyflex.com/e/</span>
        <input
          className="input"
          value={handle}
          onChange={(e) => setHandle(e.target.value.replace(/\s+/g, ''))}
          placeholder={suggestion || 'yourname'}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={32}
          style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </div>

      <div style={{ minHeight: 18, marginBottom: 10 }}>{feedback}</div>

      {suggestion && !handle && (
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.75rem', marginBottom: 10 }}
          onClick={() => setHandle(suggestion)}
        >
          Use "{suggestion}"
        </button>
      )}

      {error && (
        <p className="text-sm" style={{ color: 'var(--danger, #b23)', marginBottom: 10 }}>{error}</p>
      )}

      <button
        className="btn btn-primary btn-full"
        disabled={busy || check.state !== 'available'}
        onClick={submit}
      >
        {busy ? 'Saving…' : 'Claim handle'}
      </button>
    </div>
  )
}
